import { VAT_RATE_PERCENT } from "../../utils/vat";

/**
 * Live PREVIEW of a special order's totals while a draft is keyed in.
 *
 * Mirrors the backend's `computeBillDiscount` + `computeTaxBreakdown` (the van-bill rule the
 * server applies to a special order too), so the figure the user sees before saving matches
 * the one the saved draft comes back with. The server stays authoritative: after a save the
 * detail page shows the stored totals, never these.
 *
 * - The bill-footer discount is a percentage of the subtotal, rounded to the satang; the
 *   amount after it is derived from the ROUNDED discount so the printed lines add up.
 * - VAT is computed on what is left after the discount.
 *   `IN` (price includes VAT): VAT is extracted, 7/107 of the net; the net IS the grand total.
 *   Anything else (`EX`, `NA`, unset): VAT is added on top at VAT_RATE_PERCENT.
 */

export interface SpecialOrderLineInput {
  unit_price: number;
  qty: number;
}

export interface SpecialOrderTotals {
  /** Lines with qty > 0. */
  lineCount: number;
  /** รวมเงิน — Σ qty × unit_price. */
  subtotal: number;
  /** ส่วนลด — subtotal × discount %, to the satang. */
  discountAmount: number;
  /** subtotal − discount. */
  afterDiscount: number;
  /** ยอดก่อน VAT */
  totalNoTax: number;
  /** VAT 7% */
  taxAmount: number;
  /** ยอดสุทธิ */
  grandTotal: number;
}

const round = (value: number, dp: number) => {
  const factor = 10 ** dp;
  return Math.round(value * factor) / factor;
};

export const lineTotal = (line: SpecialOrderLineInput): number =>
  round(line.unit_price * line.qty, 4);

export function computeSpecialOrderTotals(
  lines: SpecialOrderLineInput[],
  discountPercent: number | null | undefined,
  taxtype: string | null | undefined,
): SpecialOrderTotals {
  const counted = lines.filter((line) => line.qty > 0);
  const subtotal = round(
    counted.reduce((sum, line) => sum + lineTotal(line), 0),
    4,
  );

  let discountAmount = 0;
  let afterDiscount = subtotal;
  if (discountPercent != null && discountPercent > 0 && subtotal > 0) {
    discountAmount = round((subtotal * discountPercent) / 100, 2);
    afterDiscount = round(subtotal - discountAmount, 4);
  }

  let taxAmount: number;
  let totalNoTax: number;
  let grandTotal: number;
  if (taxtype === "IN") {
    taxAmount = round((afterDiscount * VAT_RATE_PERCENT) / (100 + VAT_RATE_PERCENT), 4);
    grandTotal = afterDiscount;
    totalNoTax = round(afterDiscount - taxAmount, 4);
  } else {
    taxAmount = round((afterDiscount * VAT_RATE_PERCENT) / 100, 4);
    totalNoTax = afterDiscount;
    grandTotal = round(afterDiscount + taxAmount, 4);
  }

  return {
    lineCount: counted.length,
    subtotal,
    discountAmount,
    afterDiscount,
    totalNoTax,
    taxAmount,
    grandTotal,
  };
}
